import type { Definition } from '../api/missions';
import type { InspectionCapture } from '../api/inspections';

/**
 * What kind of stop a capture's own label names (InspectionTour.java, never
 * touched here — this reads the Catalan words it already writes): the base
 * overview slot, a kit piece indoors or outdoors, a few pieces common enough
 * to caption on their own (a chair, a board, a light, a flag), an objective,
 * or one of a force's own roles. `other` is anything the label does not
 * match, which falls back to the tour's own `expect` alone.
 */
export type StopKind =
  | 'slot'
  | 'kit_indoor'
  | 'kit_outdoor'
  | 'chair'
  | 'board'
  | 'lights'
  | 'flag'
  | 'objective'
  | 'guard'
  | 'garrison'
  | 'static_weapon'
  | 'patrol'
  | 'vehicle'
  | 'command_post'
  | 'twist'
  | 'other';

/** The four groups a tour's captures fall into, in the order a slideshow steps through them. */
export type GroupKind = 'base' | 'objective' | 'force' | 'twist';

export interface Caption {
  /** What was photographed: the tour's own label, its trailing "· vista" dropped. */
  what: string;
  /** Why this shot is worth taking: the rule or feature it checks. Null when the kind is unmapped. */
  why: string | null;
  /** What to look at in the picture. Null when the kind is unmapped. */
  look: string | null;
  /** The tour's own «Hauries de veure: …», kept as the fallback when why/look are null. */
  expect: string | null;
}

export interface CaptureGroup {
  kind: GroupKind;
  key: string;
  title: string;
  captures: InspectionCapture[];
}

/** One step of the slideshow: a photo, or a context card between two photo groups. */
export type ReviewItem =
  | { type: 'capture'; capture: InspectionCapture }
  | { type: 'context'; kind: GroupKind; key: string; title: string; facts: string[] };

/**
 * The QC classifier's own badge text for one flag (build item 1): shown next
 * to a capture before a person opens it, from the platform's own reading of
 * the watcher's stats, never a guess made here. `duplicateOfUploadId` fills
 * in "#N" when the flag is `duplicate`.
 */
export function qcBadgeText(flag: string, duplicateOfUploadId: number | null): string {
  switch (flag) {
    case 'empty':
      return 'Possiblement buida';
    case 'dark':
      return 'Fosca';
    case 'duplicate':
      return duplicateOfUploadId ? `Repetida de #${duplicateOfUploadId}` : 'Repetida';
    default:
      return flag;
  }
}

/**
 * The next capture after `from` that carries no review yet (build item 2's
 * "N" shortcut): wraps around the whole slideshow once, so pressing it from
 * the last unreviewed capture returns to the first rather than doing nothing.
 * Null when every capture already carries a review.
 */
export function nextUnreviewedIndex(items: ReviewItem[], from: number): number | null {
  const n = items.length;
  for (let step = 1; step <= n; step++) {
    const idx = (from + step) % n;
    const it = items[idx];
    if (it.type === 'capture' && !it.capture.reviewedBy) return idx;
  }
  return null;
}

const FORCE_ROLE_KIND: Record<string, StopKind> = {
  guard: 'guard',
  garrison: 'garrison',
  static: 'static_weapon',
  patrol: 'patrol',
  vehicle: 'vehicle',
  command: 'command_post',
  hq: 'command_post',
};

const stripVista = (label: string) => label.replace(/\s*·\s*vista\s*$/, '').trim();

/** The stop kind a capture's own label names. */
export function classifyLabel(label: string): StopKind {
  const clean = stripVista(label);
  if (clean.startsWith('Ranura inicial')) return 'slot';
  if (clean.startsWith('Objectiu:')) return 'objective';
  if (clean.startsWith('Gir')) return 'twist';
  if (clean.startsWith('Força:')) {
    const role = clean.slice('Força:'.length).trim().split(/\s+/)[0]?.toLowerCase() ?? '';
    return FORCE_ROLE_KIND[role] ?? 'other';
  }
  const cls = /\(([^)]+)\)/.exec(clean)?.[1]?.toLowerCase() ?? '';
  if (cls.includes('chair')) return 'chair';
  if (cls.includes('board')) return 'board';
  if (cls.includes('light') || cls.includes('lamp')) return 'lights';
  if (cls.includes('flag')) return 'flag';
  return clean.includes(' dins ') ? 'kit_indoor' : 'kit_outdoor';
}

const CAPTIONS: Partial<Record<StopKind, { why: string; look: string }>> = {
  slot: {
    why: 'Comprovar que la ranura de sortida existeix i el jugador hi surt col·locat.',
    look: 'Que estigui dret, a terra, al lloc de sortida.',
  },
  kit_indoor: {
    why: 'Comprovar que la peça està ben col·locada dins l’edifici, sense travessar parets.',
    look: 'Que estigui dreta, dins la sala, sense travessar parets, de cara al que toca.',
  },
  kit_outdoor: {
    why: 'Comprovar que la peça està ben col·locada a l’exterior.',
    look: 'Que toqui a terra, no floti ni s’enfonsi, i estigui ben orientada.',
  },
  chair: {
    why: 'Comprovar que la cadira té sentit on és.',
    look: 'Que estigui dreta sobre les potes, no de costat ni enfonsada.',
  },
  board: {
    why: 'Comprovar que el tauler o la pissarra es planta dret.',
    look: 'Que estigui dret, amb potes, no ajagut a terra.',
  },
  lights: {
    why: 'Comprovar que el llum il·lumina cap al lloc correcte.',
    look: 'Cap a on apunta, i si té sentit des d’allà.',
  },
  flag: {
    why: 'Comprovar que la bandera es planta dreta i visible.',
    look: 'Que oni dreta, no doblegada ni enfonsada.',
  },
  objective: {
    why: 'Comprovar que l’objecte de la missió té sentit on és.',
    look: 'El lloc, el que hi ha al voltant, i si l’objectiu hi encaixa.',
  },
  guard: {
    why: 'Comprovar que la guàrdia vigila el que li toca vigilar.',
    look: 'Quants homes, on són, i si vigilen el que diu la seva feina.',
  },
  garrison: {
    why: 'Comprovar que la guarnició ocupa el lloc que li toca.',
    look: 'Si els homes són dins o en punts de defensa, no en un camp buit.',
  },
  static_weapon: {
    why: 'Comprovar que l’arma estàtica té un bon camp de tir.',
    look: 'Cap a on apunta, i si veu el que hauria de defensar.',
  },
  patrol: {
    why: 'Comprovar que la patrulla segueix una ruta amb sentit.',
    look: 'Els punts de la ruta i si passen per on tocaria.',
  },
  vehicle: {
    why: 'Comprovar que el vehicle és on toca i no ha quedat encallat.',
    look: 'Que toqui a terra, dreta sobre les rodes o erugues.',
  },
  command_post: {
    why: 'Comprovar que el lloc de comandament té sentit i està protegit.',
    look: 'Si té gent i equip a prop, en un lloc defensable.',
  },
  twist: {
    why: 'Comprovar que el gir fa el que diu que fa.',
    look: 'Els efectes del gir en el moment que hauria d’activar-se.',
  },
};

/**
 * The runtime tour's own stop kind (base_overview, kit_indoor, kit_outdoor,
 * board, chair, light, flag, objective_overview, objective_thing,
 * force_guard, force_garrison, force_static, force_patrol, force_vehicle,
 * command_post, twist — fn_inspectBuild.sqf, TestModeFiles' RUNNER), mapped
 * onto the same why/look text as the guessed {@link StopKind} where one
 * fits, plus its own for the base's wide overview shot, which the guessed
 * classifier never distinguishes from a single piece.
 */
const KIND_CAPTIONS: Partial<Record<string, { why: string; look: string }>> = {
  base_overview: {
    why: 'Comprovar que la base es veu sencera i té sentit des de fora.',
    look: 'Que hi hagi les peces que toquen, ben plantada, sense cap forat evident.',
  },
  kit_indoor: CAPTIONS.kit_indoor,
  kit_outdoor: CAPTIONS.kit_outdoor,
  board: CAPTIONS.board,
  chair: CAPTIONS.chair,
  light: CAPTIONS.lights,
  flag: CAPTIONS.flag,
  objective_overview: CAPTIONS.objective,
  objective_thing: CAPTIONS.objective,
  force_guard: CAPTIONS.guard,
  force_garrison: CAPTIONS.garrison,
  force_static: CAPTIONS.static_weapon,
  force_patrol: CAPTIONS.patrol,
  force_vehicle: CAPTIONS.vehicle,
  command_post: CAPTIONS.command_post,
  // A scripted recipe's own scene stop (night_raid's tower, sleeping tent,
  // guardroom, officer's room and reaction post — fn_inspectBuild.sqf's §12.2
  // block): a group of characters the tour did not build through the generic
  // force loop, so it carries no force_* kind of its own.
  // Its own caption here: `group` is a tour kind, not a StopKind the label
  // classifier guesses, so it has no entry in CAPTIONS.
  group: {
    why: 'Comprovar que el grup fa el que li toca en aquest punt de l’escena.',
    look: 'Quants homes hi ha, la seva postura, i si encaixen amb el que hi hauria de ser.',
  },
  twist: CAPTIONS.twist,
};

/** A short Catalan name for a stop kind, shown as a badge next to a capture. */
const KIND_LABELS: Record<string, string> = {
  base_overview: 'Vista general de la base',
  kit_indoor: 'Peça interior',
  kit_outdoor: 'Peça exterior',
  board: 'Tauler',
  chair: 'Cadira',
  light: 'Llum',
  flag: 'Bandera',
  objective_overview: 'Vista general de l’objectiu',
  objective_thing: 'Element de l’objectiu',
  force_guard: 'Guàrdia',
  force_garrison: 'Guarnició',
  force_static: 'Arma estàtica',
  force_patrol: 'Patrulla',
  force_vehicle: 'Vehicle',
  command_post: 'Lloc de comandament',
  group: 'Grup',
  twist: 'Gir',
};

/** The stop kind's own badge text, or the raw value when it names one the mapping does not know yet. */
export function kindLabel(kind: string | null | undefined): string | null {
  if (!kind) return null;
  return KIND_LABELS[kind] ?? kind;
}

/**
 * The three-part caption for one capture: what, why, and what to look at.
 * The runtime's own stop kind drives it when the capture carries one (a
 * build with the framing work landed); a build that predates it, or the
 * older placement-report tour, which sends neither, falls back to the
 * guessed classification from the label text, or to the tour's own expect
 * when even that names nothing mapped.
 */
export function captionFor(capture: InspectionCapture): Caption {
  const byKind = capture.stopKind ? KIND_CAPTIONS[capture.stopKind] : undefined;
  const mapped = byKind ?? CAPTIONS[classifyLabel(capture.label ?? '')];
  return {
    what: stripVista(capture.label ?? ''),
    why: mapped?.why ?? null,
    look: mapped?.look ?? null,
    expect: capture.expect,
  };
}

function groupOf(label: string): { kind: GroupKind; key: string; title: string } {
  const clean = stripVista(label);
  if (clean.startsWith('Objectiu:')) {
    const title = clean
      .slice('Objectiu:'.length)
      .replace(/,\s*de prop.*/, '')
      .trim();
    return { kind: 'objective', key: `objective:${title}`, title };
  }
  if (clean.startsWith('Força:')) return { kind: 'force', key: 'force', title: 'Les forces' };
  if (clean.startsWith('Gir')) return { kind: 'twist', key: 'twist', title: 'Els girs' };
  return { kind: 'base', key: 'base', title: 'La base' };
}

/**
 * Every capture, split into the groups a slideshow steps through — the base,
 * each objective, the forces, the twists — in the order their first capture
 * appears, each keeping its own captures in the tour's order.
 */
export function groupCaptures(captures: InspectionCapture[]): CaptureGroup[] {
  const order: string[] = [];
  const byKey = new Map<string, CaptureGroup>();
  for (const c of captures) {
    const g = groupOf(c.label ?? '');
    let group = byKey.get(g.key);
    if (!group) {
      group = { kind: g.kind, key: g.key, title: g.title, captures: [] };
      byKey.set(g.key, group);
      order.push(g.key);
    }
    group.captures.push(c);
  }
  return order.map((k) => byKey.get(k)!);
}

const MEANS: Record<string, string> = {
  foot: 'a peu',
  vehicle: 'amb vehicle',
  helicopter: 'amb helicòpter',
  boat: 'amb barca',
  deployed: 'ja desplegats',
};

function distance(a?: number[], b?: number[]): number | null {
  if (!a || !b || a.length < 2 || b.length < 2) return null;
  return Math.round(Math.hypot(a[0] - b[0], a[1] - b[1]));
}

function baseFacts(def: Definition | undefined): string[] {
  const facts: string[] = [];
  if (!def) return facts;
  const base = def.points?.base;
  if (base?.kind) facts.push(`La base: ${base.kind}${base.label ? ` (${base.label})` : ''}.`);
  for (const o of def.objectives ?? []) {
    const d = distance(base?.position, o.position);
    if (d !== null) facts.push(`A ${d} m de ${o.title ?? o.id}.`);
  }
  const vehicles = def.sides?.players?.vehicles ?? [];
  if (vehicles.length > 0) {
    facts.push(`Transport disponible: ${vehicles.map((v) => v.label ?? v.className).join(', ')}.`);
  }
  const means = def.points?.insertion?.means;
  if (means) {
    const decided = def.decisionMatrix?.insertion?.by;
    facts.push(
      `Com s’entra: ${MEANS[means] ?? means}${decided ? ` (ho decideix ${decided})` : ''}.`,
    );
  }
  facts.push('Fotos de la ranura de sortida i de cada peça de la base.');
  return facts;
}

function objectiveFacts(def: Definition | undefined, title: string): string[] {
  const facts: string[] = [];
  const o = def?.objectives?.find((x) => (x.title ?? x.id) === title);
  if (o) {
    facts.push(`Tipus: ${o.type}.`);
    const d = distance(def?.points?.base?.position, o.position);
    if (d !== null) facts.push(`A ${d} m de la base.`);
  }
  facts.push(`Fotos de «${title}» i el seu terreny.`);
  return facts;
}

function forceFacts(def: Definition | undefined): string[] {
  const facts: string[] = [];
  const enemy = def?.sides?.enemy;
  if (enemy)
    facts.push(`L’enemic: ${enemy.faction} (${enemy.side}), força ${enemy.strength ?? '—'}.`);
  facts.push('Fotos de cada força enemiga, un cop desplegada.');
  return facts;
}

function twistFacts(def: Definition | undefined): string[] {
  const twists = def?.twists ?? [];
  return twists.map((t) => (t.body ? `${t.title}: ${t.body}` : t.title));
}

/**
 * The whole slideshow: a context card before each group of captures — the
 * mission's own facts about what is about to be seen, to help judge the
 * photos that follow — then the group's captures, in order. Twists carry no
 * capture of their own yet, so their card, when the mission has any, is
 * appended at the end rather than skipped.
 */
export function buildReviewItems(
  definition: Definition | undefined,
  captures: InspectionCapture[],
): ReviewItem[] {
  const groups = groupCaptures(captures);
  const items: ReviewItem[] = [];
  for (const g of groups) {
    const facts =
      g.kind === 'base'
        ? baseFacts(definition)
        : g.kind === 'objective'
          ? objectiveFacts(definition, g.title)
          : g.kind === 'force'
            ? forceFacts(definition)
            : twistFacts(definition);
    items.push({ type: 'context', kind: g.kind, key: g.key, title: g.title, facts });
    for (const c of g.captures) items.push({ type: 'capture', capture: c });
  }
  if (!groups.some((g) => g.kind === 'twist')) {
    const facts = twistFacts(definition);
    if (facts.length > 0)
      items.push({ type: 'context', kind: 'twist', key: 'twist', title: 'Els girs', facts });
  }
  return items;
}
